// Compliance notice strip. Used as a persistent banner on AI + clinical
// surfaces (chat, interactions graph, pill identifier, food + meals) so
// the disclaimer is in front of the user every session, not just on a
// one-time consent gate.
//
// Per Dr. Marlee Grounds (UAT 2026-05-20):
//   "Need a disclaimer that states the user is talking to an AI and
//    something compliance related like this is general medical
//    information and the final decision should be made with your
//    provider and health team."

import type { ReactNode } from 'react';

interface Props {
  /** Variant changes the icon + opening clause. */
  variant: 'ai' | 'clinical';
  /** Optional extra context line, e.g. "Severity ratings come from RxNav." */
  detail?: ReactNode;
}

export function ComplianceNotice({ variant, detail }: Props): JSX.Element {
  const lead =
    variant === 'ai' ? 'You are talking to an AI helper.' : 'This is general information.';
  return (
    <div className="compliance-notice" role="note" aria-label="Important information">
      <span aria-hidden="true" className="compliance-notice__icon">
        <svg width="20" height="20" viewBox="0 0 24 24" focusable="false">
          <title>Notice</title>
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1.2" fill="currentColor" />
        </svg>
      </span>
      <p className="compliance-notice__body">
        <strong>{lead}</strong> Always talk to your doctor or pharmacist before you change anything.
        {detail ? <> {detail}</> : null}
      </p>
    </div>
  );
}
